import { KeyboardDoubleArrowRight } from '@mui/icons-material';
import { Box, Button, Drawer, IconButton, Paper, Stack, Theme, Toolbar, Typography, useMediaQuery, useTheme } from '@mui/material';
import { clone } from 'ramda';
import { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { applicationSlice } from '../../redux/ApplicationSlice';
import { robotDataSlice } from '../../redux/RobotDataSlice';
import { robotSlice } from '../../redux/RobotSlice';
import { projectSlice } from '../../redux/ProjectSlice';
import { useAppDispatch, useAppSelector } from '../../redux/ReduxHooks';
import { RootState } from '../../redux/Store';
import { ApplicationActions, DEFAULT_SELECTED_ROBOT, RobotActions } from '../../utils/constants';
import { readConnectionPreferences } from '../../hooks/storage/useConnectionPreferences';
import { trackConnectSelected } from '../../analytics/bpsEvents';
import WirelessIcon from '../Icons/Connection/WirelessIcon';
import WiredIcon from '../Icons/Connection/WiredIcon';

const ConnectionDrawer = () => {
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const { t } = useTranslation();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const isMediumScreen = useMediaQuery(theme.breakpoints.down('md'));
  const { CONNECTED } = RobotActions.STATE;
  const { CONNECTION_DRAWER, CONNECTED_DRAWER } = ApplicationActions.Drawer;

  const [open, setOpen] = useState(false);
  const openDrawer = useAppSelector((state) => state.project.openDrawer);
  const { robotState } = useAppSelector((state) => state.robot);
  const { userRobotColor } = useSelector((state: RootState) => state.robotData);

  useEffect(() => {
    if (openDrawer !== CONNECTION_DRAWER) {
      setOpen(false);
      return;
    }

    if (robotState === CONNECTED) {
      dispatch(projectSlice.actions.onOpenDrawer(CONNECTED_DRAWER));
      return;
    }

    setOpen(true);
  }, [dispatch, openDrawer, robotState, CONNECTED, CONNECTED_DRAWER, CONNECTION_DRAWER]);

  const handleClose = () => {
    dispatch(projectSlice.actions.onOpenDrawer(null));
  };

  const handleOpenBluetoothConnectionDialog = () => {
    dispatch(projectSlice.actions.onOpenDrawer(null));

    const connectionPreferences = readConnectionPreferences();
    if (connectionPreferences.skipBluetoothInfo) {
      const color = userRobotColor;
      trackConnectSelected(RobotActions.STATE.CONNECTION_TYPE.BLE);
      dispatch(robotDataSlice.actions.onUpdateRobotType(DEFAULT_SELECTED_ROBOT));
      dispatch(
        robotSlice.actions.onUpdateRobotStateAndConnectionType({
          robotState: RobotActions.STATE.CONNECT,
          connectionType: RobotActions.STATE.CONNECTION_TYPE.BLE,
        })
      );
      dispatch(robotDataSlice.actions.onUpdateUserRobotColor(color));
      return;
    }

    const _openDialog = clone(ApplicationActions.Dialogs.CONNECT_INFO);
    _openDialog.options.selectedRobot = DEFAULT_SELECTED_ROBOT.type;
    dispatch(applicationSlice.actions.onOpenDialog(_openDialog));
  };

  const handleOpenSerialConnectionDialog = () => {
    dispatch(projectSlice.actions.onOpenDrawer(null));

    const connectionPreferences = readConnectionPreferences();
    if (connectionPreferences.skipUsbInfo) {
      const color = userRobotColor;
      trackConnectSelected(RobotActions.STATE.CONNECTION_TYPE.SERIAL);
      dispatch(robotDataSlice.actions.onUpdateRobotType(DEFAULT_SELECTED_ROBOT));
      dispatch(
        robotSlice.actions.onUpdateRobotStateAndConnectionType({
          robotState: RobotActions.STATE.CONNECT,
          connectionType: RobotActions.STATE.CONNECTION_TYPE.SERIAL,
        })
      );
      dispatch(robotDataSlice.actions.onUpdateUserRobotColor(color));
      return;
    }

    const _openDialog = clone(ApplicationActions.Dialogs.CONNECT_SERIAL);
    dispatch(applicationSlice.actions.onOpenDialog(_openDialog));
  };

  const drawerSx = {
    flexShrink: 0,
    zIndex: (theme: Theme) => theme.zIndex.drawer + 2,
    '& .MuiDrawer-paper': {
      width: isSmallScreen ? '100%' : isMediumScreen ? '600px' : '750px',
      boxSizing: 'border-box',
      bottom: 0,
      top: 'unset',
      bgcolor: 'background.paper',
      overflow: 'hidden',
    },
  };

  return (
    <Drawer anchor="right" open={open} onClose={handleClose} sx={drawerSx}>
      <Toolbar />
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: 'background.paper',
          overflow: 'hidden',
        }}
      >
        {/* Drawer Title Section */}
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          padding={1.75}
          position="relative"
          sx={{
            width: '100%',
            flexShrink: 0,
            '&::after': {
              content: '""',
              position: 'absolute',
              bottom: 0,
              left: '50%',
              transform: 'translateX(-50%)',
              width: '95%',
              height: '2px',
              backgroundColor: 'accent.main',
            },
          }}
        >
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="sectionTitle">
              <Trans i18nKey="connectionDrawer.connection-options">Connection Options</Trans>
            </Typography>
          </Box>
          <IconButton onClick={handleClose} aria-label={t('aria-label.close', 'close')} sx={{ position: 'absolute', right: 8 }}>
            <KeyboardDoubleArrowRight />
          </IconButton>
        </Box>

        {/* Scrollable Content Area */}
        <Box
          sx={{
            flexGrow: 1,
            overflowY: 'auto',
            overflowX: 'hidden',
            bgcolor: 'background.paper',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            px: 2,
          }}
        >
          <Box width="100%" maxWidth="92%" marginTop={isSmallScreen ? '8px' : '17px'}>
            <Paper elevation={2}>
              <Box padding={isSmallScreen ? '16px' : '24px'}>
                <Stack direction={'row'} alignItems={'center'} spacing={2}>
                  <WirelessIcon
                    sx={{
                      width: isSmallScreen ? '50px' : isMediumScreen ? '100px' : '150px',
                      height: isSmallScreen ? '50px' : isMediumScreen ? '100px' : '150px',
                    }}
                  />
                  <Stack height="100%" textAlign="center" direction="column" justifyContent="flex-start" spacing={2} sx={{ flex: 1 }}>
                    <Typography
                      variant="subHeaderTitle"
                      sx={{
                        fontSize: isSmallScreen ? '16px' : isMediumScreen ? '18px' : '20px',
                      }}
                    >
                      <Trans i18nKey="connectionDrawer.bluetooth">Wireless Connection</Trans>
                    </Typography>
                    <Typography>
                      <Trans i18nKey="connectionDrawer.bluetooth-instructions">Power on your Drive Unit and keep it close to your device</Trans>
                    </Typography>
                    <Button onClick={handleOpenBluetoothConnectionDialog} variant="contained" size="large">
                      <Trans i18nKey="connectionDrawer.connect-bluetooth">Connect</Trans>
                    </Button>
                  </Stack>
                </Stack>
              </Box>
            </Paper>
          </Box>

          <Box width="100%" maxWidth="92%" marginTop="17px" marginBottom="17px">
            <Paper elevation={2}>
              <Box padding={isSmallScreen ? '16px' : '24px'}>
                <Stack direction={'row'} alignItems={'center'} spacing={2}>
                  <WiredIcon
                    sx={{
                      width: isSmallScreen ? '50px' : isMediumScreen ? '100px' : '150px',
                      height: isSmallScreen ? '50px' : isMediumScreen ? '100px' : '150px',
                    }}
                  />
                  <Stack height="100%" textAlign="center" direction="column" justifyContent="flex-start" spacing={2} sx={{ flex: 1 }}>
                    <Typography
                      variant="subHeaderTitle"
                      sx={{
                        fontSize: isSmallScreen ? '16px' : isMediumScreen ? '18px' : '20px',
                      }}
                    >
                      <Trans i18nKey="connectionDrawer.usb">Wired Connection</Trans>
                    </Typography>
                    <Typography>
                      <Trans i18nKey="connectionDrawer.usb-instructions">Plug in your Drive Unit into your device with a compatible USB cable</Trans>
                    </Typography>
                    <Button onClick={handleOpenSerialConnectionDialog} variant="contained" size="large">
                      <Trans i18nKey="connectionDrawer.connect-usb">Connect</Trans>
                    </Button>
                  </Stack>
                </Stack>
              </Box>
            </Paper>
          </Box>
        </Box>
      </Box>
    </Drawer>
  );
};

export default ConnectionDrawer;
